import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "sonlyo.auth";

/**
 * The API is a Cloudflare Worker, which may live on a different origin than the
 * Pages deployment. Browsers increasingly refuse third-party cookies, so the
 * tokens the API returns are also kept here and sent as a bearer header. The
 * cookies the Worker sets still work on same-site deployments; both paths are
 * accepted server-side.
 */
export function readTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveTokens(data) {
  if (!data?.access_token) return;
  try {
    // The listener is kept alongside the tokens so a returning visitor can be
    // shown their library immediately, instead of watching a spinner while
    // /auth/me travels to the edge and back. The call still happens; it just
    // no longer blocks the first paint.
    const { access_token: _a, refresh_token: _r, ...user } = data;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        access_token: data.access_token,
        refresh_token: data.refresh_token ?? readTokens()?.refresh_token ?? null,
        user: Object.keys(user).length > 0 ? user : (readTokens()?.user ?? null),
      })
    );
  } catch {
    /* storage unavailable (private mode), cookies still apply */
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Without a timeout a request that never answers never rejects either, and
  // callers that wait on one wait forever. The player is the case that made
  // this visible: looking up a track's audio would hang, and the queue sat on
  // a song it could not start. Twenty-five seconds is past the Worker's own
  // limits, so this only fires when something is genuinely wrong.
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const token = readTokens()?.access_token;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A single in-flight refresh shared by every request that hits a 401 at once.
let refreshing = null;

async function refreshSession() {
  const stored = readTokens();
  const refresh_token = stored?.refresh_token;
  // With nothing stored there is no session to renew, and asking anyway costs
  // an anonymous visitor a guaranteed 401 on the landing page.
  if (!stored?.access_token && !refresh_token) {
    throw new Error("no session to refresh");
  }
  const { data } = await axios.post(
    `${API}/auth/refresh`,
    refresh_token ? { refresh_token } : {},
    { withCredentials: true }
  );
  saveTokens(data);
  return data.access_token;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isAuthCall = original?.url?.includes("/auth/refresh") || original?.url?.includes("/auth/login");

    if (error.response?.status !== 401 || original?._retried || isAuthCall) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      refreshing = refreshing ?? refreshSession().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${token}`;
      }
      return api(original);
    } catch {
      clearTokens();
      return Promise.reject(error);
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;

/**
 * Message to show the user for a failed request. Prefers whatever the API
 * said; falls back to something actionable rather than a bare "Error", which
 * makes a report like "it fails when I press the button" impossible to act on.
 */
export function apiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiError(detail);
  if (err?.response?.status === 403) return "You do not have permission for this.";
  if (err?.code === "ECONNABORTED" || err?.message === "Network Error") {
    return "Could not reach the server. Check your connection and try again.";
  }
  return err?.message || "Something went wrong. Please try again.";
}

/**
 * Absolute URL for a media path the API returned.
 *
 * The API and the web app are different origins, so a bare "/api/..." path in
 * an <img src> would resolve against the app and 404.
 */
/**
 * Asks the artwork provider for a size that matches the box it goes in.
 *
 * Covers arrive as 1000x1000 JPEGs of roughly 350KB, and they are drawn at
 * forty pixels in a track row and a couple of hundred on a browse card. A
 * page of thirty of them was pulling ten megabytes down a phone connection to
 * paint thumbnails. Deezer serves the same image at any size from the same
 * path, so the request simply asks for the right one; anything that is not
 * one of its URLs is returned untouched.
 */
export function coverUrl(url, px = 250) {
  if (!url || typeof url !== "string") return url || "";
  if (!url.includes("dzcdn.net")) return url;
  return url.replace(/\/\d+x\d+-/, `/${px}x${px}-`);
}

export function mediaUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}
